import { IModelBaseId, FileResponse } from "../../api/api";
import saveAs from "file-saver";

export function mergeArraysHashmap<T extends IModelBaseId>(
  oldHashmap: { [key: string]: T[] },
  updatedHashmap: { [key: string]: T[] }
) {
  if (!oldHashmap || Object.keys(oldHashmap).length === 0) {
    return updatedHashmap;
  }

  let clonedOldHashmap = { ...oldHashmap };

  Object.keys(updatedHashmap).forEach((updatedKey) => {
    let oldArray = clonedOldHashmap[updatedKey];
    if (oldArray === undefined) {
      oldArray = [];
    }
    let clonedOldArray = [...oldArray];

    let updatedArray = updatedHashmap[updatedKey];

    if (oldArray) {
      updatedArray.forEach((u) => {
        let index = clonedOldArray.findIndex((o) => o.id === u.id);
        merge(index, clonedOldArray, u);
      });

      clonedOldHashmap[updatedKey] = clonedOldArray;
    } else {
      clonedOldHashmap[updatedKey] = updatedArray.filter((ua) => !ua.isDeleted);
    }
  });

  return clonedOldHashmap;
}

export function mergeArraysWithHashmap<T extends IModelBaseId>(
  oldHashmap: { [key: string]: T[] },
  newDataArray: T[],
  hashmapKeyGetter: (elem: T) => string
) {
  let clonedOldHashmap = { ...oldHashmap };

  newDataArray.forEach((dataCollection) => {
    if (hashmapKeyGetter(dataCollection) === undefined) {
      return {};
    }

    let oldSettingsArray = clonedOldHashmap[hashmapKeyGetter(dataCollection)];
    if (oldSettingsArray === undefined) {
      oldSettingsArray = [];
    }
    let clonedOldArray = [...oldSettingsArray];

    if (oldSettingsArray) {
      if (
        clonedOldArray.find((x) => x.id === dataCollection.id) === undefined
      ) {
        clonedOldArray.push(dataCollection);
      }

      clonedOldHashmap[hashmapKeyGetter(dataCollection)] = clonedOldArray;
    }
  });
  return clonedOldHashmap;
}

export function mergeArrays<T extends IModelBaseId>(
  oldArray: T[],
  updatedArray: T[]
) {
  if (!updatedArray || updatedArray.length === 0) {
    return oldArray;
  }

  if (!oldArray || oldArray.length === 0) {
    return updatedArray;
  }

  let clonedOld = [...oldArray];
  updatedArray.forEach((updated) => {
    const index = clonedOld.findIndex((old) => old.id === updated.id);
    merge<T>(index, clonedOld, updated);
  });

  return clonedOld.filter((elem) => !elem.isDeleted);
}

export function mergeArraysReplaceNewer<T extends IModelBaseId>(
  oldArray: T[],
  updatedArray: T[],
  idFn: (t: T) => string,
  newerValidationFn: (oldElem: T, newElem: T) => boolean
) {
  if (!oldArray || oldArray.length === 0) {
    return updatedArray;
  }

  let clonedOld = [...oldArray];

  updatedArray.forEach((updated) => {
    const index = clonedOld.findIndex((old) => idFn(old) === idFn(updated));

    if (index > -1) {
      //Check if we should replace
      if (updated.isDeleted === true) {
        //Remove if it has been deleted - todo: get the newest from the server when we make a delete functionality
        clonedOld.splice(index, 1);
      } else {
        //Replace existing if newer else ignore
        const old = clonedOld[index];
        if (newerValidationFn(old, updated)) {
          merge(index, clonedOld, updated);
        }
      }
    } else {
      //Just add it - its the first for this animal
      merge(index, clonedOld, updated);
    }
  });

  return clonedOld.filter((elem) => !elem.isDeleted);
}

export function mergeArraysNoUnique<T>(
  oldArray: T[],
  updatedArray: T[],
  idFn: (t: T) => string,
  deletedFn: (t: T) => boolean
) {
  if (!oldArray || oldArray.length === 0) {
    return updatedArray;
  }

  let clonedOld = [...oldArray];
  updatedArray.forEach((updated) => {
    const index = clonedOld.findIndex((old) => idFn(old) === idFn(updated));
    mergeNoUnique<T>(index, clonedOld, updated);
  });

  return clonedOld.filter((elem) => !deletedFn(elem));
}

export function merge<T extends IModelBaseId>(
  index: number,
  clonedOld: T[],
  updated: T
) {
  if (index > -1) {
    if (updated.isDeleted) {
      clonedOld.splice(index, 1);
    } else {
      clonedOld[index] = updated;
    }
  } else {
    if (!updated.isDeleted) {
      clonedOld.push(updated);
    }
  }
}

function mergeNoUnique<T>(index: number, clonedOld: T[], updated: T) {
  if (index > -1) {
    clonedOld[index] = updated;
  } else {
    clonedOld.push(updated);
  }
}

export function updateValueInArray<T extends IModelBaseId>(
  array: T[],
  value: T
) {
  return array.map((item, index) => {
    if (item.id !== value.id) {
      // This isn't the item we care about - keep it as-is
      return item;
    }

    // Hack to fix: Spread types may only be created from object types - https://github.com/Microsoft/TypeScript/issues/22687
    let i = item as any;
    let v = value as any;

    // Otherwise, this is the one we want - return an updated value
    return {
      ...i,
      ...v,
    };
  });
}

export function addValueToArray<T>(array: T[], value: T) {
  const arr = [...array, value];
  return arr;
}

export function upsertValueInArray<T extends IModelBaseId>(
  old: T[],
  updated: T
) {
  const index = old.findIndex((o) => o.id === updated.id);
  return upsert(old, updated, index);
}

export function upsertValueInArrayCustomId<T extends IModelBaseId>(
  old: T[],
  updated: T,
  idFn: (t: T) => string
) {
  const index = old.findIndex((o) => idFn(o) === idFn(updated));
  return upsert(old, updated, index);
}

export function upsertValueInHashmap<T extends IModelBaseId>(
  oldHashmap: { [key: string]: T[] },
  updated: T,
  key: string
) {
  let clonedOldHashmap = { ...oldHashmap };

  let oldArray = clonedOldHashmap[key];
  if (!oldArray) {
    oldArray = [];
    clonedOldHashmap[key] = oldArray;
  }
  let clonedArray = [...oldArray];

  const index = clonedArray.findIndex((o) => o.id === updated.id);
  let updatedArray = upsert(clonedArray, updated, index);

  clonedOldHashmap[key] = updatedArray;
  return clonedOldHashmap;
}

export function saveCSVFilefAs(fileResponse: FileResponse, fileName: string) {
  let blob = new Blob([fileResponse.data]);
  saveAs(blob, fileName);
}

export function upsertValueInArrayNoUnique<T>(
  old: T[],
  updated: T,
  idFn: (t: T) => string
) {
  const index = old.findIndex((o) => idFn(o) === idFn(updated));
  return upsert(old, updated, index);
}

export function upsertMultipleValuesInArray<T extends IModelBaseId>(
  old: T[],
  updates: T[]
) {
  updates.forEach((update) => {
    const index = old.findIndex((o) => o.id === update.id);
    old = upsert(old, update, index);
  });
  return old;
}

function upsert<T>(old: T[], updated: T, index: number) {
  //Need to be a clone, since redux have problems when mutating the state
  let clonedOld = [...old];
  if (index > -1) {
    clonedOld[index] = updated;
  } else {
    clonedOld.push(updated);
  }

  return clonedOld;
}

export function removeValueFromArray<T extends IModelBaseId>(
  arr: T[],
  id: number
): T[] {
  const index = arr.findIndex((x) => x.id === id);
  return remove(arr, index);
}

export function removeKeyFromHashmap<T extends IModelBaseId>(
  hashmap: { [key: string]: T[] },
  key: string
): { [key: string]: T[] } {
  let clonedHashmap = { ...hashmap };
  delete clonedHashmap[key];
  return clonedHashmap;
}

export function removeValueFromHashmap<T extends IModelBaseId>(
  hashmap: { [key: string]: T[] },
  id: number,
  key: string
): { [key: string]: T[] } {
  let clonedHashmap = { ...hashmap };
  let clonedArray = [...clonedHashmap[key]];

  const index = clonedArray.findIndex((x) => x.id === id);
  clonedArray = remove(clonedArray, index);
  clonedHashmap[key] = clonedArray;

  return clonedHashmap;
}

export function removeMultipleValuesFromArray<T extends IModelBaseId>(
  arr: T[],
  ids: number[]
): T[] {
  ids.forEach((id) => {
    const index = arr.findIndex((x) => x.id === id);
    arr = remove(arr, index);
  });
  return arr;
}

export function removeValueFromArrayNoUnique<T>(
  arr: T[],
  id: string,
  idFn: (t: T) => string
): T[] {
  const index = arr.findIndex((x) => idFn(x) === id);
  return remove(arr, index);
}

function remove<T>(arr: T[], index: number) {
  if (index === -1) {
    return arr;
  }
  const arra = [...arr];
  arra.splice(index, 1);

  return arra;
}
